import { createSlice, PayloadAction, CaseReducer } from '@reduxjs/toolkit';
import { RtcEngine } from '../../services/agora/rtcEngine';
import { RtmEngine } from '../../services/agora/rtmEngine';
import { ResponseBoard } from '../../services/api/models/response/responseBoard';
import { ResponseEvent } from '../../services/api/models/response/responseEvent';
import {
  EventRanking,
  ResponseJoinLive,
  ResponseStartLive,
} from '../../services/api/models/response/responseLive';
import { ResponseUser } from '../../services/api/models/response/responseUser';
import { MediaDevice } from './components/LiveSettings/LiveSettings';
import { LiveState } from './LiveContainer';
import { LiveComment } from './models/liveComment';
import { MessageUser } from './models/messageUser';
import { ResponseUserPairWantJoined } from '../../services/api/models/response/responseVoicePlus';

const defaultLiveState: LiveState = {
  isShowTermModal: false,
  isShowGuidelineModal: false,
  isShowSettingModal: false,
  isShowVoiceGuestModal: false,
  isShowSettingPairModal: false,
  isUseStreamKey: false,
  isEnded: false,
  isBanEnded: undefined,
  isInLive: false,
  isLogin: false,
  isEnableGift: true,
  isAcceptPair: false,
  joinId: '',
  liverLv: 0,
  gottenDiamond: 0,
  gottenDiamondPool: 0,
  isDiamondAnim: false,
  memberLastestUpdate: 0,
  channelMemberList: [],
  liveComments: [],
  mediaDevices: [],
  listPair: [],
  role: 'host',
  isAudioMuted: false,
  isMirrorEnable: false,
  isShowListBoard: false,
  showEventModal: false,
  isShowMissionModal: false,
  isShowLiveAudienceMobileSideModal: false,
  isLive: false,
  isSendRequestPair: false,
  isShowRemoveUserPairModal: false,
  isShowNoticePairModal: false,
  mediaDevicesGuest: [],
  resizeStatus: false,
};

// show term of delivery modal
const toggleTermModal: CaseReducer<any, PayloadAction<boolean>> = (
  state: LiveState,
  action: PayloadAction<boolean>,
): void => {
  state.isShowTermModal = action.payload;
};

// show term of delivery modal
const toggleGuidelineModal: CaseReducer<any, PayloadAction<boolean>> = (
  state: LiveState,
  action: PayloadAction<boolean>,
): void => {
  state.isShowGuidelineModal = action.payload;
};

// show setting modal
const toggleSettingsModal: CaseReducer<any, PayloadAction<boolean>> = (
  state: LiveState,
  action: PayloadAction<boolean>,
): void => {
  state.isShowSettingModal = action.payload;
};
const toggleResizeStatus: CaseReducer<any, PayloadAction<void>> = (
  state: LiveState,
): void => {
  state.resizeStatus = !state.resizeStatus;
};

const updateEnableGift: CaseReducer<LiveState, PayloadAction<boolean>> = (
  state,
  action,
): void => {
  state.isEnableGift = action.payload;
};

const updateIsUseStreamKey: CaseReducer<LiveState, PayloadAction<boolean>> = (
  state,
  action,
): void => {
  state.isUseStreamKey = action.payload;
};

const updateEventRankings: CaseReducer<
  LiveState,
  PayloadAction<EventRanking[]>
> = (state, action) => {
  state.eventRankings = action.payload;
};

// update list of media devices
const updateListMediaDevices: CaseReducer<
  any,
  PayloadAction<Array<MediaDevice>>
> = (state: LiveState, action: PayloadAction<Array<MediaDevice>>): void => {
  state.mediaDevices = action.payload;
  state.inputVideoDevice = action.payload.find(
    (media) => media.kind === 'videoinput',
  )?.id;
  state.inputAudioDevice = action.payload.find(
    (media) => media.kind === 'audioinput',
  )?.id;
  state.outputAudioDevice = action.payload.find(
    (media) => media.kind === 'audiooutput',
  )?.id;
};

// change video device input
const changeVideoInput: CaseReducer<any, PayloadAction<string | undefined>> = (
  state: LiveState,
  action: PayloadAction<string | undefined>,
): void => {
  state.inputVideoDevice = action.payload;
};

// change audio device input
const changeAudioInput: CaseReducer<any, PayloadAction<string>> = (
  state: LiveState,
  action: PayloadAction<string>,
): void => {
  state.inputAudioDevice = action.payload;
};

// change audio device input
const changeAudioOutput: CaseReducer<any, PayloadAction<string>> = (
  state: LiveState,
  action: PayloadAction<string>,
): void => {
  state.outputAudioDevice = action.payload;
};

// start live stream
const startLive: CaseReducer<LiveState, PayloadAction<ResponseStartLive>> = (
  state,
  action,
): void => {
  const liveData = action.payload;
  state.liveData = liveData;
  state.pairType = liveData.radio?.pairType;
  state.userData = liveData.user;
  state.liverLv = liveData.user.level;
  state.isInLive = true;
  state.role = 'host';
  state.rtcEngine = new RtcEngine();
  state.rtmEngine = new RtmEngine();
  state.isAudioMuted = false;
  state.isMirrorEnable = true;
  state.eventRankings = liveData.eventRankings;
};

// join live stream
const joinLive: CaseReducer<any, PayloadAction<ResponseJoinLive>> = (
  state: LiveState,
  action: PayloadAction<ResponseJoinLive>,
): void => {
  const joinLiveData = action.payload;

  joinLiveData.live.radio?.pairType === 'none'
    ? (state.isAcceptPair = false)
    : (state.isAcceptPair = true);

  state.joinId = joinLiveData.id;
  state.userData = joinLiveData.user;
  state.liveData = joinLiveData.live;
  state.liverLv = joinLiveData.live.user.level;
  state.isInLive = true;
  state.pairType = joinLiveData.live.radio?.pairType;
  state.role = 'audience';
  state.rtcEngine = new RtcEngine();
  state.rtmEngine = new RtmEngine();
  state.eventRankings = joinLiveData.live.eventRankings;
};

// reset live state
const resetLiveData: CaseReducer<any, PayloadAction<void>> = (
  state: LiveState,
): void => {
  state.isInLive = false;
  state.isEnded = false;
  state.isBanEnded = undefined;
  state.liveData = undefined;
  state.userData = undefined;
  state.liverLv = 0;
  state.userCoin = undefined;
  state.isShowSettingModal = false;
  state.isShowTermModal = false;
  state.isShowGuidelineModal = false;
  state.liveComments = [];
  state.isAudioMuted = false;
  state.isMirrorEnable = false;
  state.joinId = '';
  state.role = 'host';
  state.rtcEngine?.removeAllListeners();
  state.rtmEngine?.removeAllListeners();
  state.rtcEngine?.destroy();
  state.rtmEngine?.destroy();
  state.isShowListBoard = false;
  state.giftTopAudience = undefined;
};

const setBannedEnd: CaseReducer<LiveState, PayloadAction<boolean>> = (
  state,
  action,
) => {
  state.isBanEnded = action.payload;
};

// end live and release resources
const endLive: CaseReducer<any, PayloadAction<boolean | undefined>> = (
  state: LiveState,
  action,
): void => {
  state.isEnded = true;
  state.isBanEnded = !!action.payload;
  state.isInLive = false;
  state.rtcEngine?.destroy();
  state.rtmEngine?.destroy();
};

// add comments to current live
const addLiveComment: CaseReducer<any, PayloadAction<LiveComment>> = (
  state: LiveState,
  action: PayloadAction<LiveComment>,
): void => {
  const message = action.payload;
  state.liveComments.push(message);
};

// add comments to current live
const toggleAudio: CaseReducer<any, PayloadAction<void>> = (
  state: LiveState,
): void => {
  state.isAudioMuted = !state.isAudioMuted;
};

// add comments to current live
const toggleMirrorMode: CaseReducer<any, PayloadAction<void>> = (
  state: LiveState,
): void => {
  state.isMirrorEnable = !state.isMirrorEnable;
};

// end live and release resources
const setGiftTopAudience: CaseReducer<
  any,
  PayloadAction<ResponseUser | undefined>
> = (
  state: LiveState,
  action: PayloadAction<ResponseUser | undefined>,
): void => {
  state.giftTopAudience = action.payload;
};

const showListBoard: CaseReducer<any, PayloadAction<boolean>> = (
  state: LiveState,
  action: PayloadAction<boolean>,
): void => {
  state.isShowListBoard = action.payload;
};

const updateLiveBoard: CaseReducer<any, PayloadAction<ResponseBoard>> = (
  state: LiveState,
  action: PayloadAction<ResponseBoard>,
): void => {
  if (state.liveData) {
    state.liveData.board = action.payload;
  }
};

const deleteLiveBoard: CaseReducer<any, PayloadAction> = (
  state: LiveState,
): void => {
  if (state.liveData) {
    state.liveData.board = undefined;
  }
};

const showEventModal: CaseReducer<
  any,
  PayloadAction<ResponseEvent | undefined>
> = (
  state: LiveState,
  action: PayloadAction<ResponseEvent | undefined>,
): void => {
  if (action.payload === undefined) {
    state.currentShowEvent = undefined;
    state.showEventModal = false;
  } else {
    state.currentShowEvent = action.payload;
    state.showEventModal = true;
  }
};

const updateLiverLevel: CaseReducer<any, PayloadAction<MessageUser>> = (
  state: LiveState,
  action: PayloadAction<MessageUser>,
): void => {
  if (
    action.payload.level &&
    action.payload.loginId === state.liveData?.user.loginId
  ) {
    state.liverLv = action.payload.level;
  }
};

const updateUserCoin: CaseReducer<any, PayloadAction<number>> = (
  state: LiveState,
  action: PayloadAction<number>,
): void => {
  state.userCoin = action.payload;
};

const toggleMissionModal: CaseReducer<any, PayloadAction<boolean>> = (
  state: LiveState,
  action: PayloadAction<boolean>,
): void => {
  state.isShowMissionModal = action.payload;
};
const toggleAudienceMobileModal: CaseReducer<any, PayloadAction<boolean>> = (
  state: LiveState,
  action: PayloadAction<boolean>,
): void => {
  state.isShowLiveAudienceMobileSideModal = action.payload;
};

const toggleVoiceGuestModal: CaseReducer<any, PayloadAction<boolean>> = (
  state: LiveState,
  action: PayloadAction<boolean>,
): void => {
  state.isShowVoiceGuestModal = action.payload;
};

const toggleSettingPairModal: CaseReducer<any, PayloadAction<boolean>> = (
  state: LiveState,
  action: PayloadAction<boolean>,
): void => {
  state.isShowSettingPairModal = action.payload;
};
const reloadMissionGiftTab: CaseReducer = (state: LiveState): void => {
  state.isReloadingMissionGiftTab = !state.isReloadingMissionGiftTab;
};

const handleTypeLive: CaseReducer<any, PayloadAction<boolean>> = (
  state: LiveState,
  action: PayloadAction<boolean>,
): void => {
  state.isLive = action.payload;
};
const changeAcceptPair: CaseReducer<any, PayloadAction<boolean>> = (
  state: LiveState,
  action: PayloadAction<boolean>,
): void => {
  state.isAcceptPair = action.payload;
};

const handleChangePairType: CaseReducer<
  any,
  PayloadAction<string | undefined>
> = (state: LiveState, action: PayloadAction<string | undefined>): void => {
  state.pairType = action.payload;
};
// handle change active button request pair
const handleDisableButtonRequest: CaseReducer<any, PayloadAction<boolean>> = (
  state: LiveState,
  action: PayloadAction<boolean>,
): void => {
  state.isAcceptPair = action.payload;
};
// handle change button request
const handleChangeButtonRequest: CaseReducer<any, PayloadAction<boolean>> = (
  state: LiveState,
  action: PayloadAction<boolean>,
): void => {
  state.isSendRequestPair = action.payload;
};
// update listPair
const updateListPair: CaseReducer<
  any,
  PayloadAction<Array<ResponseUserPairWantJoined>>
> = (
  state: LiveState,
  action: PayloadAction<Array<ResponseUserPairWantJoined>>,
): void => {
  state.listPair = action.payload;
};
// Update User Pair
const updateUserPair: CaseReducer<any, PayloadAction<any>> = (
  state: LiveState,
  action: PayloadAction<any>,
): void => {
  state.userPair = action.payload;
};
const showAllowChoicePair: CaseReducer<any, PayloadAction<boolean>> = (
  state: LiveState,
  action: PayloadAction<boolean> | undefined,
): void => {
  state.isShowAllowChoice = action?.payload;
};

const toggleRemoveUserPairModal: CaseReducer<any, PayloadAction<boolean>> = (
  state: LiveState,
  action: PayloadAction<boolean>,
): void => {
  state.isShowRemoveUserPairModal = action.payload;
};

const toggleNoticeApprovePairModal: CaseReducer<any, PayloadAction<boolean>> = (
  state: LiveState,
  action: PayloadAction<boolean>,
): void => {
  state.isShowNoticePairModal = action.payload;
};
const updateListMediaDevicesGuest: CaseReducer<
  any,
  PayloadAction<Array<MediaDevice>>
> = (state: LiveState, action: PayloadAction<Array<MediaDevice>>): void => {
  state.mediaDevicesGuest = action.payload;

  state.inputAudioDeviceGuest = action.payload.find(
    (media) => media.kind === 'audioinput',
  )?.id;
};
// change audio device input guest
const changeAudioInputGuest: CaseReducer<any, PayloadAction<string>> = (
  state: LiveState,
  action: PayloadAction<string>,
): void => {
  state.inputAudioDeviceGuest = action.payload;
};
const toggleProfileModal: CaseReducer<any, PayloadAction<boolean>> = (
  state: LiveState,
  action: PayloadAction<boolean>,
): void => {
  state.isShowProfileModal = action.payload;
};

const toggleConfirmBlockedModal: CaseReducer<any, PayloadAction<boolean>> = (
  state: LiveState,
  action: PayloadAction<boolean>,
): void => {
  state.isShowConfirmBlockedModal = action.payload;
};

const liveSlice = createSlice({
  name: 'live',
  initialState: defaultLiveState,
  reducers: {
    toggleTermModal,
    toggleGuidelineModal,
    toggleSettingsModal,
    updateListMediaDevices,
    changeVideoInput,
    changeAudioInput,
    changeAudioOutput,
    startLive,
    joinLive,
    endLive,
    setBannedEnd,
    addLiveComment,
    resetLiveData,
    toggleAudio,
    toggleMirrorMode,
    setGiftTopAudience,
    showListBoard,
    updateLiveBoard,
    deleteLiveBoard,
    updateLiverLevel,
    showEventModal,
    updateUserCoin,
    toggleMissionModal,
    toggleVoiceGuestModal,
    toggleSettingPairModal,
    reloadMissionGiftTab,
    toggleAudienceMobileModal,
    handleTypeLive,
    changeAcceptPair,
    handleDisableButtonRequest,
    updateListPair,
    updateUserPair,
    showAllowChoicePair,
    handleChangeButtonRequest,
    toggleRemoveUserPairModal,
    toggleNoticeApprovePairModal,
    handleChangePairType,
    updateListMediaDevicesGuest,
    changeAudioInputGuest,
    toggleProfileModal,
    toggleConfirmBlockedModal,
    toggleResizeStatus,
    updateEnableGift,
    updateEventRankings,
    updateIsUseStreamKey,
  },
});

export const { actions } = liveSlice;
export const liveReducer = liveSlice.reducer;
